import { Injectable } from "@angular/core";
import { EntityService } from "@app/entity/services/entity.service";
import { AlulaReceiverGroup } from "@app/project/model/alula-receiver-group";
import { AlulaReceiverGroupDbService } from "@app/project/services/alula-receiver-group-db.service";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { ActionWord } from "@app/shared/resources/action-word";
import { checkCurrentID } from "@app/shared/resources/data-actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable } from "rxjs";
import { concatMap, exhaustMap, map, switchMap, tap } from "rxjs/operators";
import { AlulaReceiverGroupActions, DataActions } from "../actions";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { createAuditLogKey, LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE } from "@app/audit-log/services/audit-log.constants";

const toAction = DataActions.toAction2();

@Injectable()
export class AlulaReceiverGroupEffects {
    readonly entityName = "Central Station";

    constructor(
        private readonly actions$: Actions,
        private readonly alulReceiverGroupDbService: AlulaReceiverGroupDbService,
        private readonly deleteConfirmationService: DeleteConfirmationService,
        private readonly entityService: EntityService,
        private readonly auditLogService: AuditLogService
    ) {}

    wrapResult(this: void, source$: Observable<AlulaReceiverGroup>) {
        return source$.pipe(map(result => ({ group: result })));
    }

    wrapBulkResult(this: void, source$: Observable<AlulaReceiverGroup[]>) {
        return source$.pipe(map(result => ({ groups: result })));
    }

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.load),
            switchMap(({ dealerId }) =>
                toAction(
                    this.alulReceiverGroupDbService
                        .getAlulaReceiverGroupsByDealerId(dealerId)
                        .pipe(this.wrapBulkResult, checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), dealerId)),
                    AlulaReceiverGroupActions.loadSuccess,
                    AlulaReceiverGroupActions.loadFailure
                )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.create),
            concatMap(({ receiverGroup, callback }) =>
                toAction(
                    this.alulReceiverGroupDbService
                        .createAlulaReceiverGroup(receiverGroup)
                        .pipe(this.wrapResult, checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), receiverGroup?.dealerId)),
                    AlulaReceiverGroupActions.createSuccessSilent,
                    AlulaReceiverGroupActions.createFailure,
                    callback
                )
            )
        )
    );

    createSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.createSuccess),
            map(DataActions.handleSuccessMessage2(ActionWord.created, null, this.entityName))
        )
    );

    createFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.createFailure),
            map(DataActions.handleErrorMessage2(ActionWord.created, this.entityName))
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.update),
            switchMap(({ receiverGroup, callback }) =>
                toAction(
                    this.alulReceiverGroupDbService
                        .updateAlulaReceiverGroup(receiverGroup, receiverGroup.id)
                        .pipe(this.wrapResult, checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), receiverGroup?.dealerId)),
                    AlulaReceiverGroupActions.updateSuccessSilent,
                    AlulaReceiverGroupActions.updateFailure,
                    callback
                )
            )
        )
    );

    updateSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.updateSuccess),
            map(DataActions.handleSuccessMessage2(ActionWord.saved, null, this.entityName))
        )
    );

    updateFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.updateFailure),
            map(DataActions.handleErrorMessage2(ActionWord.saved, this.entityName))
        )
    );

    deleteConfirmation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.deleteConfirmation),
            exhaustMap(payload =>
                this.deleteConfirmationService.open$(
                    payload,
                    AlulaReceiverGroupActions.deleteConfirmationDismiss,
                    AlulaReceiverGroupActions.deleteItem,
                    payload.receiverGroup.name
                )
            )
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.deleteItem),
            exhaustMap(({ receiverGroup, logData }) =>
                toAction(
                    this.alulReceiverGroupDbService.deleteAlulaReceiverGroup(receiverGroup.id).pipe(
                        map(_ => ({ group: receiverGroup })),
                        checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), receiverGroup?.dealerId),
                        tap(_ => {
                            this.auditLogService.logEvent(
                                createAuditLogKey(LOG_CATEGORY.COMPANY, LOG_TYPE.CENTRAL_STATIONS, LOG_SUB_TYPE.DELETE),
                                logData
                            );
                        })
                    ),
                    AlulaReceiverGroupActions.deleteSuccess,
                    AlulaReceiverGroupActions.deleteFailure
                )
            )
        )
    );

    deleteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.deleteSuccess),
            map(DataActions.handleSuccessMessage2(ActionWord.deleted, null, this.entityName))
        )
    );

    deleteFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.deleteFailure),
            map(DataActions.handleErrorMessage2(ActionWord.saved, this.entityName))
        )
    );

    upsertSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverGroupActions.upsertSuccess),
            map(DataActions.handleSuccessMessage2(ActionWord.saved, null, this.entityName))
        )
    );
}
